import '../product-card/product-card-display';
import emit from '../../common/events';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { StoreState } from '../../store/store.interface';
import { ViewType, ViewTypeLabel } from './interfaces/search.interface';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { selectView } from './store/search.selector';
import { toggleView } from './utils/search.util';
import {
  ViewSelectorColor,
  ViewSelectorColorType,
  ViewSelectorStyle,
  ViewSelectorStyleType
} from './interfaces/search-results-view.interface';

/**
 * An element to toggle search results view
 *
 * @csspart view - Show search result in list or card view.
 *
 */
@customElement('mhecomm-search-results-view')
export class SearchResultsView extends connect(store)(BaseComponent) {
  /**
   * Outline color for the button.
   */
  @property()
  color: ViewSelectorColorType = ViewSelectorColor.DARK;

  /**
   * Selector style for the button.
   */
  @property()
  selectorStyle:  ViewSelectorStyleType = ViewSelectorStyle.OUTLINE;

  /**
   * Title CSS class.
   */
  @property()
  titleClass = 'fw-bold mb-2';

  /**
   * This property will decide whether search result need to show in List or Card view.
   * Currently this is being used in PLP search result page.
   */
  @state()
  view: ViewType = ViewTypeLabel.LIST;

  renderComp() {
    return html`
      <div class="d-flex flex-column" id="change-view-container" data-automation-id="change-view-container">
        <label class="${this.titleClass}">View</label>
        <div>
          <div class="btn-group" role="group" aria-label="Search Result Toggle View">
            <button
              type="button"
              class="btn ${this.getButtonStyle(ViewTypeLabel.LIST)}"
              aria-label="List View"
              aria-current="${(this.view === ViewTypeLabel.LIST)}"
              @click=${() => this.switchView(ViewTypeLabel.LIST)}
              data-bs-toggle="tooltip"
              data-bs-html="true"
              title="List view"
              data-bs-placement="bottom"
              id="list-view-btn"
              data-automation-id="list-view-btn"
            >
            ${this.uxtIconReady ? html`<uxt-icon icon="list"></uxt-icon>` : nothing}
            </button>
            <button
              type="button"
              class="btn ${this.selectorStyle === 'outline' ? 'ms-2' : ''} ${this.getButtonStyle(ViewTypeLabel.CARD)}"
              aria-label="Card View"
              aria-current="${(this.view === ViewTypeLabel.CARD)}"
              @click=${() => this.switchView(ViewTypeLabel.CARD)}
              data-bs-toggle="tooltip"
              data-bs-html="true"
              title="Card view"
              data-bs-placement="bottom"
              id="card-view-btn"
              data-automation-id="card-view-btn"
            >
              ${this.uxtIconReady ? html`<uxt-icon icon="grid-fill"></uxt-icon>` : nothing}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  switchView(view: ViewType) {
    emit({
      element: this,
      name: 'view',
      detail: {
        eventName: `${view} view`,
        comp: 'view',
        label: `Switch to ${view} View`,
      },
      dataLayer: true,
    });
    store.dispatch(toggleView(view));
  }

  stateChanged(state: StoreState) {
    const viewType = selectView(state);
    this.view = viewType ? viewType : ViewTypeLabel.LIST;
  }

  updated() {
    this.bsInit('[data-bs-toggle="tooltip"]', window.bootstrap?.Tooltip);
  }

  /**
   * Returns the button class based on the view and the selected options (color, style).
   *
   * @param view
   */
  private getButtonStyle(view: ViewTypeLabel): string {
    const isSelected: boolean = (this.view === view);

    if (this.selectorStyle === ViewSelectorStyle.FILL) {
      return isSelected ? `btn-${this.color}` : `btn-outline-${this.color}`;
    }

    return isSelected ? `btn-outline-${this.color}` : '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-results-view': SearchResultsView;
  }
}
